<template>
  <div>
    <vue-headful :title="this.setPageTitle('scoringProfiles')" />
    <loading :active="loadingDetails || loadingInsights" :color="'blue'" is-full-page loader="spinner"> </loading>
    <b-container class="my-4">
      <!-- TITLE -->
      <b-row class="mb-4">
        <b-col>
          <div class="d-flex">
            <span class="mr-2">
              <a
                @click.prevent="
                  $router.push({
                    name: 'scoringProfilesList',
                  })
                "
                class="pointer"
              >
                <span class="mr-2 align-middle"><i class="fas fa-chevron-left fa-lg"></i></span></a
            ></span>
            <div class="mr-auto">
              <h1 class="h5">
                {{ getScoringProfileName }}
              </h1>
            </div>
            <div>
              <b-button class="mr-1" size="sm" variant="primary" data-test="onProductIdsChangeBtn" @click="editProfile"
                >{{ $t('scoringProfileDetails.editProfile') }}
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>

      <hr />

      <b-row>
        <b-col class="scoring-descriptions mb-3">
          {{ getScoringProfileDescription }}
        </b-col>
      </b-row>

      <b-row v-if="!loadingDetails" class="mt-2">
        <b-col>
          <profile-features :data-coverage="getScoringProfileCoverage"></profile-features>
        </b-col>
      </b-row>

      <b-row v-if="getExportPermission">
        <b-col>
          <div class="float-right my-3">
            <action-button
              :options="exportButtonOptions"
              :is-processing="isDownloading"
              :title="$t('search.label.exportResults')"
            ></action-button>
          </div>
        </b-col>
      </b-row>

      <div v-else class="my-4"></div>

      <b-row>
        <b-col>
          <insight
            :insightsProducts="getInsightProducts"
            :insightsPerimeter="getInsightMapped"
            @show-distribution-score="showDistributionScoreData"
          ></insight>
        </b-col>
      </b-row>

      <b-row v-if="!loadingInsights">
        <b-col class="mb-4">
          <scoring-coverage-filters
            :issuerOptions="getCoverageIssuersList"
            :hasFiltersApplied="someCoverageFiltersApplied"
            :countFiltersSelected="countCoverageFiltersApplied"
            :issuersFilter="coverageFilters.issuers"
            :sliderScoreF="coverageFilters.scoreF"
            :sliderScoreT="coverageFilters.scoreT"
            :compatibilityFilter="coverageFilters.compatibility"
            :searchBar="searchBar"
            @reset-coverage-filters="resetFilters"
            @update:searchBar="filterByString"
            @on-apply-filters="onApplyFilters"
          ></scoring-coverage-filters>
        </b-col>
      </b-row>

      <b-row v-if="!loadingInsights">
        <b-col v-if="!productsFound">
          <no-content
            :icon-name="'exclamation-circle'"
            :icon-class="['mb-3', 'fa-3x']"
            :text="$t('common.noScoringProfilesFound')"
          ></no-content>
        </b-col>
        <b-col v-else>
          <insights-table
            :tableItems="getFilteredProducts"
            @click-table-row="onClickTableRow"
            @show-distribution-score="showDistributionScoreData"
            @show-product-score-data="showProductScoreData"
            @filters-data="showIncompatibleScoreFilters"
          />
        </b-col>
      </b-row>
      <custom-modal
        v-if="insightsModalValue != null"
        :id="'insightsDetails'"
        v-model="insightsModalValue"
        @on-click-table-row="onClickTableRow"
      />
      <incompatible-filters-modal
        v-if="_incompatibleFiltersValue != null"
        :filters-data="_incompatibleFiltersValue"
        modal-id="incompatibleFilters"
      />
    </b-container>
  </div>
</template>

<script>
import ProfileFeatures from '../../components/ScoringProfiles/ProfileFeatures.vue'
import Insight from '../../components/ScoringProfiles/Insight.vue'
import ScoringCoverageFilters from '../../components/ScoringProfiles/ScoringCoverageFilters.vue'
import InsightsTable from '../../components/ScoringProfiles/InsightsTable.vue'
import NoContent from '../../components/Lib/NoContent'
import CustomModal from '../../components/Lib/CustomModal'
import IncompatibleFiltersModal from '../../components/ScoringProfiles/IncompatibleFiltersModal.vue'
import { createNamespacedHelpers } from 'vuex'
import { mathRoundPercentage, round } from '../../utils/formatter'
import ActionButton from '../../components/Lib/ActionButton.vue'
import {
  FILE_TYPE,
  FILE_NAME_EXPORT_SID,
  FILE_NAME_EXPORT_INVESTMENT_CERTIFICATES,
  FILE_NAME_EXPORT_PRODUCTS,
  INIT_COVERAGE_FILTERS_INSIGHTS,
} from '../../settings/scoringProfilesSettings'

import { downloadFile } from '../../utils/fileManager'
import { deepDiffMapper, VALUE_CHANGED } from '../../utils/objDifference'

const { mapGetters, mapActions } = createNamespacedHelpers('scoringProfilesDetails')

export default {
  components: {
    Insight,
    ScoringCoverageFilters,
    InsightsTable,
    ProfileFeatures,
    CustomModal,
    ActionButton,
    NoContent,
    IncompatibleFiltersModal,
  },
  props: {
    scoringProfileId: {
      type: String,
      default: null,
    },
    modelsList: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      loadingDetails: false,
      loadingInsights: false,
      insightsModalValue: null,
      isMopDistribution: null,
      currentProductKey: null,
      isDownloading: false,
      coverageFilters: INIT_COVERAGE_FILTERS_INSIGHTS(),
      searchBar: '',
    }
  },
  computed: {
    ...mapGetters([
      'getInsightMapped',
      'getInsightProducts',
      'getExportPermission',
      'getCoverageIssuersList',
      'getFilteredProducts',
    ]),

    productsFound() {
      return this.getFilteredProducts.length > 0
    },
    getScoringProfileName() {
      return this.$store.getters['scoringProfilesCommon/getScoringProfileName']
    },
    getScoringProfileDescription() {
      return this.$store.getters['scoringProfilesCommon/getScoringProfileDescription']
    },
    getScoringProfileCoverage() {
      return this.$store.getters['scoringProfilesCommon/getProfileCoverage']
    },
    distributionModalFields() {
      return [
        !this.isMopDistribution
          ? { key: 'issuerName', label: this.$t('search.resultTable.issuer'), sortable: true }
          : null,
        {
          key: this.isMopDistribution ? 'investmentOptionName' : 'name',
          label: this.isMopDistribution ? this.$t('common.option') : this.$t('common.product'),
          sortable: true,
        },
        {
          key: 'scoreResult',
          label: this.$t('common.scoring'),
          sortable: true,
          class: ['text-right'],
          formatter: (val) => (typeof val === 'number' && !isNaN(val) ? mathRoundPercentage(val) : '-'),
        },
      ]
    },
    productScoreModalFields() {
      return [
        { key: 'investmentOptionName', label: this.$t('common.option'), sortable: true },
        { key: 'compatibilityType', label: this.$t('common.compatibility'), sortable: true },
        {
          key: 'totalScore',
          label: this.$t('common.scoring'),
          sortable: true,
          class: ['text-right'],
          formatter: (val) => (typeof val === 'number' && !isNaN(val) ? mathRoundPercentage(val) : '-'),
        },
      ]
    },
    isInvestmentCertificates() {
      return this.$store.getters.getSelectedAssetClass === 'investment-certificates'
    },
    exportButtonOptions() {
      let options = []
      if (this.isDownloading) {
        options.push({ label: this.$t('search.label.exportResultsDownloading'), variant: 'warning' })
      } else if (this.isInvestmentCertificates) {
        // Investment Certificates have a single export (SID-based); keep one "Product" entry that
        // invokes the SID export.
        options.push({
          label: this.$t('search.label.exportResultsProduct'),
          callback: () => this.exportData(true),
          variant: 'primary',
        })
      } else {
        options.push({
          label: this.$t('search.label.exportResultsProduct'),
          callback: () => this.exportData(false),
          variant: 'primary',
        })
        options.push({
          label: this.$t('search.label.exportResultInvestmentOptions'),
          callback: () => this.exportData(true),
          variant: 'primary',
        })
      }
      return options
    },
    someCoverageFiltersApplied() {
      return this.countCoverageFiltersApplied > 0
    },
    countCoverageFiltersApplied() {
      const defaultFilters = INIT_COVERAGE_FILTERS_INSIGHTS()
      const appliedFilters = this.coverageFilters
      const diff = deepDiffMapper(appliedFilters, defaultFilters).filterByType(VALUE_CHANGED).result
      //any field pairs as xxxF and xxxT count as 1
      const values = Object.keys(diff).filter(
        (key) => !(key.endsWith('F') && Object.keys(diff).includes(key.slice(0, -1) + 'T'))
      )

      let filtersApplied = values.length

      if (this.searchBar !== '') {
        filtersApplied = filtersApplied + 1
      }

      return filtersApplied
    },
    _incompatibleFiltersValue: {
      get() {
        return this.incompatibleFiltersValue
      },
      set(newValue) {
        this.incompatibleFiltersValue = newValue
      },
    },
  },
  methods: {
    ...mapActions(['loadInsight', 'filteredProducts']),
    editProfile() {
      this.$router.push(`/scoringProfilesEdit/${this.scoringProfileId}`)
    },
    onClickTableRow(value) {
      this.openProductDetails(value, this.scoringProfileId)
    },
    showDistributionScoreData(value, item) {
      const obj =
        item != null
          ? ((this.currentProductKey = item.productScore.productKey || null),
            { title: item.name, subtitle: item.productScore.scoringProfileName })
          : { title: this.getScoringProfileName, subtitle: '' }

      // range:score_range is the key in vue-components on column_click
      // elements is the key representing inv. options distribution on column_click
      this.isMopDistribution = value.range === 'score_range' && 'elements' in value

      const items = this.distributionScoreItems(value)

      this.insightsModalValue = {
        title: obj.title,
        subtitle: obj.subtitle,
        subHeader: {
          number: items.length,
          label: this.$t('common.modal.elementsInRange'),
        },
        otherInfo: {
          label1:
            this.$t('common.modal.rangeFilter') +
            ' ' +
            mathRoundPercentage(value.min) +
            ' - ' +
            mathRoundPercentage(value.max),
        },
        placeholder: this.$t('common.searchInvestmentOption'),
        items: items,
        fields: this.distributionModalFields,
        labelDisplayClass: ['d-inline'],
      }

      this.$nextTick(() => {
        this.$bvModal.show('insightsDetails')
      })
    },
    distributionScoreItems(value) {
      let elements = this.isMopDistribution ? value.elements : this.getInsightMapped || []

      let temp = this.isMopDistribution
        ? elements.filter((el) => round(el.totalScore, 8) >= round(value.min, 8))
        : elements.filter((el) => round(el.score, 8) >= round(value.min, 8))

      if (!value.isLastColumns) {
        temp = this.isMopDistribution
          ? temp.filter((el) => round(el.totalScore, 8) < round(value.max, 8))
          : temp.filter((el) => round(el.score, 8) < round(value.max, 8))
      }

      const items = this.isMopDistribution
        ? temp.map((el) => {
            return {
              id: null,
              name: this.currentProductKey.name,
              issuerName: this.currentProductKey.issuerName,
              issuerCode: this.currentProductKey.issuerCode,
              referenceDate: this.currentProductKey.referenceDate,
              investmentOptionName: el.investmentOptionName,
              scoreResult: el.totalScore,
            }
          })
        : temp.map((el) => {
            return {
              id: el.productId,
              name: el.name,
              issuerName: el.issuerName,
              issuerCode: el.issuerCode,
              referenceDate: el.referenceDate,
              scoreResult: el.score,
            }
          })

      return items
    },
    showProductScoreData(value, item) {
      const obj =
        item != null
          ? ((this.currentProductKey = item.productScore.productKey || null),
            { title: item.name, subtitle: item.productScore.scoringProfileName })
          : { title: this.getScoringProfileName, subtitle: '' }

      this.insightsModalValue = {
        title: obj.title,
        subtitle: obj.subtitle,
        subHeader: {
          number: value.scoreResults.length,
          label: this.$t('common.options'),
        },
        otherInfo: {
          label1:
            value.scoreResults.filter((el) => el.compatibilityType === 1).length +
            ' ' +
            this.$t('common.compatibleOptions') +
            ', ' +
            (value.scoreResults.filter((el) => el.compatibilityType === 0).length +
              ' ' +
              this.$t('common.notCompatibleOptions')),
        },
        placeholder: this.$t('common.searchInvestmentOption'),
        items: this.productScoreItems(value),
        fields: this.productScoreModalFields,
        labelDisplayClass: ['d-inline'],
      }

      this.$nextTick(() => {
        this.$bvModal.show('insightsDetails')
      })
    },
    productScoreItems(value) {
      if (!value) return []

      const items = value.scoreResults.map((item) => {
        return {
          name: value.productKey.name,
          issuerCode: value.productKey.issuerCode,
          referenceDate: value.productKey.referenceDate,
          investmentOptionName: item.investmentOptionName,
          compatibilityType:
            item.compatibilityType === 1 ? this.$t('common.compatible') : this.$t('common.notCompatible'),
          totalScore: item.totalScore,
        }
      })

      return items
    },
    showIncompatibleScoreFilters(value) {
      this._incompatibleFiltersValue = value

      this.$nextTick(() => {
        this.$bvModal.show('incompatibleFilters')
      })
    },
    exportData(includeSids) {
      if (!this.getExportPermission) return
      this.isDownloading = true

      const endpoint = includeSids
        ? '/api/export/sids/scoringProfile/' + this.scoringProfileId
        : '/api/export/products/scoringProfile/' + this.scoringProfileId

      const objectToSend = {
        scoringProfileId: this.scoringProfileId,
      }

      const fileName = includeSids
        ? this.isInvestmentCertificates
          ? FILE_NAME_EXPORT_INVESTMENT_CERTIFICATES
          : FILE_NAME_EXPORT_SID
        : FILE_NAME_EXPORT_PRODUCTS

      downloadFile(this, 'post', endpoint, objectToSend, FILE_TYPE, 'isDownloading', fileName, 'Generic Error')
    },
    resetFilters() {
      this.coverageFilters = INIT_COVERAGE_FILTERS_INSIGHTS()
      this.searchBar = ''
      const filters = {
        queryString: this.searchBar,
        ...this.coverageFilters,
      }
      this.filteredProducts(filters)
    },
    onApplyFilters(value) {
      this.coverageFilters = value
      const filters = {
        queryString: this.searchBar,
        ...this.coverageFilters,
      }
      this.filteredProducts(filters)
    },
    filterByString(value) {
      this.searchBar = value
      const filters = {
        queryString: this.searchBar,
        ...this.coverageFilters,
      }
      this.filteredProducts(filters)
    },
  },
  created() {
    this.loadingDetails = true
    this.loadingInsights = true

    const canViewScoringProfilesModels = this.$store.getters['can_ViewScoringProfileModels']

    if (!this.modelsList && canViewScoringProfilesModels) {
      this.$store.dispatch('scoringProfilesModelsList/loadModelsListToAssociate').catch((err) => {
        this.httpErrorsHandler(err)
      })
    }

    this.$store.dispatch('scoringProfilesFilters/loadFiltersCatalog').catch((err) => {
      this.httpErrorsHandler(err)
    })

    this.$store
      .dispatch('scoringProfileEditor/loadScoringProfile', {
        id: this.scoringProfileId,
        action: '',
      })
      .catch((err) => {
        this.httpErrorsHandler(err)
      })
      .finally(() => {
        this.loadingDetails = false
      })

    this.loadInsight(this.scoringProfileId)
      .catch((err) => {
        this.httpErrorsHandler(err)
      })
      .finally(() => {
        this.loadingInsights = false
      })
  },
}
</script>

<style lang="scss" scoped>
.scoring-descriptions {
  color: var(--gray);
}
.row-loading {
  min-height: 150px;
}

.variant-orange {
  color: var(--primary);
}
</style>
